import { useMemo } from 'react'
import { numericFormatter, removeNumericFormat } from 'react-number-format'
import styled from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'

import { ArrowLeftIcon } from '../icons'

import type { ClassNameProps } from '@root/shared/react'
import type { NumericFormatProps } from 'react-number-format'

// Comma used for mobile keyboards
const DECIMAL_SEPARATORS = [',', '.']

const NUMBERS = Array.from({ length: 9 })
  .fill(undefined)
  .map((_, idx) => idx + 1)

type Props = {
  value: string
  onChange: (value: string) => void
  decimalScale?: number
} & ClassNameProps
export const Keyboard: React.FC<Props> = ({
  value: rawValue,
  onChange,
  className,
  decimalScale = 32,
}) => {
  const numericProps = useMemo(
    (): NumericFormatProps => ({
      decimalScale: decimalScale,
      thousandSeparator: ',',
      decimalSeparator: '.',
      allowedDecimalSeparators: DECIMAL_SEPARATORS,
    }),
    [decimalScale],
  )
  const haptics = useHapticFeedback()
  const handleChange = (raw: string) => {
    const numString = removeNumericFormat(
      raw,
      getDefaultChangeMeta(raw),
      numericProps,
    )

    onChange(numericFormatter(numString, numericProps))
  }

  const appendChar = (char: string) => {
    haptics.impactOccurred('light')

    const raw = rawValue === '0' ? char : rawValue + char
    const rawZero = raw[0] === '.' ? `0${raw}` : raw

    handleChange(rawZero)
  }

  const handleBackspace = () => {
    haptics.impactOccurred('light')

    const raw = rawValue.slice(0, Math.max(0, rawValue.length - 1)) || '0'

    handleChange(raw)
  }

  return (
    <Root className={className}>
      {NUMBERS.map((num) => (
        <Button
          key={num}
          onClick={() => {
            appendChar(num.toString())
          }}
        >
          {num}
        </Button>
      ))}
      <Button
        onClick={() => {
          appendChar('.')
        }}
      >
        .
      </Button>
      <Button
        onClick={() => {
          appendChar('0')
        }}
      >
        0
      </Button>
      <Button onClick={handleBackspace}>
        <ArrowLeftIcon />
      </Button>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(4, 54px);
  gap: 2px;
`

const Button = styled.button`
  display: grid;
  cursor: pointer;
  border-radius: 16px;
  font-size: 20px;
  font-weight: 500;
  line-height: 16px;
  justify-content: center;
  align-items: center;
  color: var(--text-12);
  border: none;
  background: transparent;
  -webkit-mask: linear-gradient(#000 0 0);
  mask: linear-gradient(#000 0 0);

  &:hover,
  &:focus {
    background: rgba(255 255 255 / 1%);
  }
  &:active {
    background: rgba(255 255 255 / 3%);
  }

  &:before {
    position: absolute;
    pointer-events: none;
    content: ' ';
    background: radial-gradient(
      68% 66% at 50% 50%,
      rgba(118, 145, 164, 0.15) 0%,
      rgba(19, 19, 19, 0) 100%
    );
    inset: 0;
  }
`

// https://github.com/s-yadav/react-number-format/blob/8de2517a1d10058254a94acd36cfa5bd62c4b73d/src/utils.tsx#L309
const getDefaultChangeMeta = (value: string) => {
  return {
    from: {
      start: 0,
      end: 0,
    },
    to: {
      start: 0,
      end: value.length,
    },
    lastValue: '',
  }
}
